import React, { useCallback, useMemo, useState, FC } from 'react';
import Image from 'react-bootstrap/Image';
import Form from 'react-bootstrap/Form';
import { Link } from 'react-router-dom';
import { format } from 'date-fns'

import type { IAction } from '../../../types/affiliates.types';
import type { IProductCardProps } from '../productCard';
import BindingButton from '../../BindingButton';
import AccountRecoveryButton from '../../AccountRecoveryButton';
import GetHintButton from '../../GetHintButton';
import './style.scss';
import { removeProtocol } from '../../../utils/netUtils';

const ProductItem: FC<IProductCardProps> = ({ data, onActivateButtonClick, onReactivateButtonClick, onGetHint, onGetAccountRecovery }) => {
  const {
    customData,
    cps,
    cpDescription,
    error,
    logId,
    linked,
    hinteable,
    metadata,
    isPreviouslyBound,
    subjectId,
    binding
  } = data;
  const [itemState, setItemState] = useState({
    conditionsAccepted: false,
    isDirty: false,
    isLoading: false
  });
  const terms = useMemo(() => customData?.find(p => p.key && p.key === 'term')?.value, [customData]);
  const redirectURLRegisteredUser = useMemo(() => customData?.find(p => p.key && p.key === 'redirectURLRegisteredUser')?.value, [customData]);
  if (!redirectURLRegisteredUser) {
    console.warn('You must set "redirectURLRegisteredUser" in cloudPay customData to enable button on linked product');
  }

  localStorage.setItem(`${cps}-terms`, JSON.stringify(terms));

  const activateButtonClickHandler = useCallback((e: any) => {
    e.preventDefault();

    if (linked && redirectURLRegisteredUser) {
      return window.open(redirectURLRegisteredUser);
    }

    setItemState(prev => ({ ...prev, isLoading: true }));
    
    if (onActivateButtonClick) {
      Promise.resolve(onActivateButtonClick())
        .catch((error) => {
          console.error('Error al activar producto:', error);
        })
        .finally(() => {
          setTimeout(() => {
            setItemState(prev => ({ ...prev, isLoading: false }));
          }, 1000);
        });
    }
  }, [linked, onActivateButtonClick, redirectURLRegisteredUser]);

  const reactivateButtonClickHandler = useCallback((e: any) => {
    e.preventDefault();
    setItemState(prev => ({ ...prev, isLoading: true }));
    
    if (onReactivateButtonClick) {
      Promise.resolve(onReactivateButtonClick())
        .catch((error) => {
          console.error('Error al reactivar producto:', error);
        })
        .finally(() => {
          setTimeout(() => {
            setItemState(prev => ({ ...prev, isLoading: false }));
          }, 1000);
        });
    }
  }, [onReactivateButtonClick]);

  const termsCheckboxClickHandler = useCallback((e: any) => {
    setItemState({
      ...itemState,
      conditionsAccepted: e.target.checked
    });
  }, [itemState]);

  const disableButton = useMemo(() => {
    if (error) {
      return true;
    }

    if (!Boolean(terms)) {
      return false;
    } else if (itemState.conditionsAccepted) {
      return false;
    } else {
      if (linked) {
        return false;
      }
      return true;
    }
  }, [error, itemState.conditionsAccepted, linked, terms]);

  const showCheck = useMemo(() => !linked && Boolean(terms) && !error, [linked, terms, error]);

  const suspendAction = useMemo(() => binding?.actions?.find((action: IAction) => action?.action === 'SUSPEND'), [binding]);

  const accomplishAction = useMemo(() => binding?.actions?.find((action: IAction) => action?.action === 'ACCOMPLISH'), [binding]);

  const serviceName = useMemo(() => {
    return cpDescription || metadata?.name || cps;
  }, [cpDescription, metadata?.name, cps]);

  const loginUrl = useMemo(() => {
    return data?.loginUrl || '';
  }, [data?.loginUrl]);

  const errorMessage = useMemo(() => {
    if (!error) {
      return '';
    }

    const baseMessage = '* Se ha producido un error en la activación de su producto.';

    if (logId) {
      return `${baseMessage} Código de error: ${error}. Identificador del error: ${logId}`;
    }

    return `${baseMessage} Identificador del error: ${error}`;
  }, [error, logId]);

  return (
    <>
      {binding.status === 'SUSPENDED' && suspendAction ?
        <div className='suspended'>
          {metadata?.image && (
            <div className='suspended-logo'>
              <Image src={metadata.image} alt={serviceName} />
            </div>
          )}
          <p>Tu servicio de <strong>{serviceName}</strong> se encuentra suspendido por deuda pendiente, desde el día {format(suspendAction.updatedAt, 'dd/MM/yyyy')}</p>
          <p>Paga tus cuentas de forma segura <a href='https://vtr.com/portaldepagos/initial'>aquí</a></p>
        </div> :
        <div className={`product-item${error ? ' product-item--error' : ''}`}>
          <div className={`${cps} ${linked ? 'bound' : ''}`}>
            <div className="content">
              <div className="logo">
                {metadata?.image && <Image src={metadata.image} alt={serviceName} />}
              </div>
              {linked ? (
                <div className="linked-message">
                  Te informamos que ya usaste el beneficio de VTR, por favor ingresa a <a href={loginUrl} target="_blank" rel="noopener noreferrer">{removeProtocol(loginUrl)}</a> con el correo{cps === 'prime' ? ' o teléfono' : ''} y contraseña que creaste en {serviceName} el día {accomplishAction ? format(accomplishAction.updatedAt, 'dd/MM/yyyy') : 'de activación'}.
                  {cps === 'netflix'
                    ? ' Si no recuerdas tu cuenta haz clic en Recuperar Cuenta para ver los datos de inicio de sesión.'
                    : ` Si no recuerdas los datos de tu cuenta ${serviceName}, llámanos al 600 800 9000 y te ayudamos en el proceso, recuerda tener tu cédula de identidad a mano.`
                  }
              </div>
              ) : (
                <div className='info'>
                  <BindingButton
                    disabled={disableButton}
                    isLoading={itemState.isLoading}
                    onClickHandler={cps === 'disney' && isPreviouslyBound && !linked ? reactivateButtonClickHandler : activateButtonClickHandler}
                    title={linked ? 'Ingresar' : 'Activar'}
                  />
                </div>
              )}
            </div>
            {showCheck &&
              <div className="footer">
                <Form.Check
                  type="checkbox"
                  id="product_condition_of_use"
                >
                  <Form.Check.Input
                    onBlur={() => setItemState({ ...itemState, isDirty: true })}
                    onChange={termsCheckboxClickHandler}
                    type="checkbox"
                    data-testid="term-checkbox"
                    className={itemState.isDirty && !itemState.conditionsAccepted ? 'error' : ''}
                  />
                  <Form.Check.Label className="checkbox_label">
                    {`Acepto los `}
                    <Link
                      to={`/vtr/${cps}/termsofuse`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {`términos y condiciones`}
                    </Link>
                  </Form.Check.Label>
                </Form.Check>
              </div>
            }
            {subjectId && <div className="footer">Tu ID de suscripción es: <span>{subjectId}</span></div>}
            <GetHintButton cps={cps} linked={linked} hinteable={hinteable} onGetHint={onGetHint} />
            <AccountRecoveryButton cps={cps} linked={linked} onGetAccountRecovery={onGetAccountRecovery} />
          </div>
          {itemState.isDirty && !itemState.conditionsAccepted && <div className="error-message">Debe aceptar los términos y condiciones</div>}
          {error && <div className="error-susbscription">{errorMessage}</div>}
        </div>}

    </>
  );
}

export default ProductItem;
